import api from '@/helpers/api'

const API_READ = `/api/branch/`
const API_UPDATE = API_READ
const API_CREATE = API_READ
const API_DELETE = API_READ
const API_DEACTIVATE = `${API_READ}deactivate/`
const API_ACTIVATE = `${API_READ}activate/`
const API_READ_ALL = `/api/branch/all/`

export default {
    namespaced: true,
    state: {
        status: '',
        branches: [],
        currUser: {},
        errMessage: '',
        meta: {
            limit: 10,
            page: 1,
            total: 1
        },
        filter: {},
        search: {}
    },
    mutations: {
        request(state) {
            state.status = 'loading'
        },
        success(state) {
            state.status = 'success'
        },
        error(state, { message }) {
            state.status = 'error'
            state.errMessage = message
        },
        read(state, { branches }) {
            state.branches = [...branches]
        },
        read_single_success(state, { branches }) {
            state.branches = branches
        },
        switch_page(state, { meta }) {
            state.meta = { ...meta }
        },
        decrease_total(state) {
            if (state.meta.total > 1) {
                state.meta.total = state.meta.total - 1;
            }
        },
        increase_total(state) {
            state.meta.total = state.meta.total + 1;
        },
        add_filter(state, { filter }) {
            state.filter = filter;
        },
        set_search(state, { search }) {
            state.search = { ...search };
        },
        clear_search(state) {
            state.search = {};
        },
    },
    actions: {
        read({ commit, state }) {

            commit('request')

            const { meta } = state
            const { filter } = state
            const { search } = state

            return new Promise((resolve, reject) => {
                api.request("get", API_READ, { meta, filter, search })
                    .then((result) => {
                        commit('read', { branches: result.data.data })

                        // Preserve total if API doesn't return it or returns 0
                        const newMeta = { ...result.data.meta };
                        const apiTotal = parseInt(newMeta.total) || 0;
                        const stateTotal = parseInt(state.meta.total) || 0;
                        const currentPage = parseInt(newMeta.page) || 1;
                        const dataCount = result.data.data.length;

                        // If API returns 0 but we have previous total, keep the previous
                        if (apiTotal === 0 && stateTotal > 0) {
                            // If we're on a page > 1 and got no data, recalculate total pages
                            if (currentPage > 1 && dataCount === 0) {
                                newMeta.total = Math.max(1, currentPage - 1);
                            } else {
                                newMeta.total = stateTotal;
                            }
                        } else if (apiTotal > 0) {
                            newMeta.total = apiTotal;
                        }

                        commit('switch_page', { meta: newMeta })
                        commit('success')
                        return resolve(result)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });
        },
        read_single({ commit }, id) {

            commit('request');

            return new Promise((resolve, reject) => {
                api.request("get", `${API_READ}${id}`)
                    .then((result) => {
                        commit('success')
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },
        deactivate({ commit }, id) {

            commit('request');

            return new Promise((resolve, reject) => {
                api.request("post", `${API_DEACTIVATE}${id}`)
                    .then((response) => {
                        commit('success')
                        return resolve(response.data)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err.response.data)
                    });
            });

        },
        activate({ commit }, id) {

            commit('request');

            return new Promise((resolve, reject) => {
                api.request("post", `${API_ACTIVATE}${id}`)
                    .then((response) => {
                        commit('success')
                        return resolve(response.data)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err.response.data)
                    });
            });

        },
        delete({ commit, dispatch, state }, id) {

            commit('request');

            // Check if this is the last item on current page (before delete)
            const itemsCount = state.branches.length;
            const currentPage = state.meta.page;
            const willBeEmpty = itemsCount === 1 && currentPage > 1;

            return new Promise((resolve, reject) => {
                api.request("delete", `${API_DELETE}${id}`)
                    .then((response) => {
                        // If page will be empty after delete, navigate to previous page first
                        if (willBeEmpty) {
                            const newMeta = { ...state.meta, page: currentPage - 1 };
                            commit('switch_page', { meta: newMeta });
                            // Decrease total pages since we deleted last item on a page
                            commit('decrease_total');
                        }

                        // Reload data
                        dispatch('read');
                        commit('success')
                        return resolve(response.data)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err.response.data)
                    });
            });

        },
        update({ commit }, { data }) {

            commit('request');

            return new Promise((resolve, reject) => {
                api.request("put", `${API_UPDATE}${data.id}`, {}, data)
                    .then((request) => {
                        commit('success');
                        return resolve(request)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });


        },
        create({ commit, state }, { data }) {

            commit('request');

            return new Promise((resolve, reject) => {

                api.request("post", `${API_CREATE}`, {}, data)
                    .then((request) => {
                        // Calculate if new item will be on a new page
                        const limit = parseInt(state.meta.limit) || 10;
                        const currentTotal = parseInt(state.meta.total) || 1;
                        const branchesCount = state.branches.length;

                        // If current page is full, new item will be on next page
                        if (branchesCount >= limit) {
                            // Increase total pages and navigate to it
                            const lastPage = currentTotal + 1;
                            const newMeta = { ...state.meta, page: lastPage, total: lastPage };
                            commit('switch_page', { meta: newMeta });
                        } else {
                            // Just increase total if needed (item will be on current/next page)
                            commit('increase_total');
                        }

                        commit('success');
                        return resolve(request)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });
        },
        read_all({ commit }, corporate_id) {
            commit('request');
            return new Promise((resolve, reject) => {
                api.request('get', `${API_READ_ALL}${corporate_id}`)
                    .then((result) => {
                        commit('success')
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err);
                    });
            });
        }
    },
    getters: {
        branches: () => state => state.branches,
    }
}
